import { css } from "@emotion/react";
import { useForm } from "@tanstack/react-form";
import { AlertCircle, CheckCircle2, Eye, EyeOff, Info } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useResetPasswordFormState } from "../hooks/useResetPasswordFormState";
import { useSiteLayoutState } from "../hooks/useSiteLayoutState";

type CompleteResetPasswordFormProps = {
	type:
		| "completeresetpassword"
		| "completeregistration"
		| "completechangepassword";
};
export const CompleteResetPasswordForm: React.FC<
	CompleteResetPasswordFormProps
> = (props) => {
	//const theme = useGlobalTheme();
	const fieldSpacing: string = "0.75rem";
	const resetPasswordFormState = useResetPasswordFormState();
	const isFormDisabled =
		resetPasswordFormState.get().isWaitingForSendResetPasswordEmail ||
		resetPasswordFormState.get().isSendResetPasswordEmailSuccessful;
	const siteLayoutState = useSiteLayoutState();
	const hasTemporaryPassword =
		!!resetPasswordFormState.get().temporaryPasswordValue;
	const disableEmail = isFormDisabled || hasTemporaryPassword;
	let pageTitle = "";
	const isResetPasswordPage = props.type === "completeresetpassword";
	const isCompleteRegistrationPage = props.type === "completeregistration";
	const isChangePasswordPage = props.type === "completechangepassword";
	if (isResetPasswordPage) {
		pageTitle = resetPasswordFormState.translate("Reset Password");
	} else if (isCompleteRegistrationPage) {
		pageTitle = resetPasswordFormState.translate("Complete Registration");
	} else if (isChangePasswordPage) {
		pageTitle = resetPasswordFormState.translate("Change Password");
	}
	const [showPassword, setShowPassword] = useState(false);
	let resetPasswordType: "change" | "reset" = "reset";
	if (isChangePasswordPage) {
		resetPasswordType = "change";
	}
	const completeResetForm = useForm({
		defaultValues: {
			email: resetPasswordFormState.get().emailValue || "",
			newPassword: resetPasswordFormState.get().newPasswordValue || "",
		},
		onSubmit: async ({ value }) => {
			resetPasswordFormState.setEmail(value.email);
			resetPasswordFormState.setNewPassword(value.newPassword);
			if (hasTemporaryPassword) {
				await siteLayoutState.resetPasswordFromEmailAsync(resetPasswordType);
				return;
			}
			await siteLayoutState.sendResetPasswordEmailAsync(resetPasswordType);
		},
	});
	const getResetFieldError = (errors: readonly unknown[]) => {
		return typeof errors[0] === "string" ? errors[0] : "";
	};

	return (
		<div css={css`max-width: 600px;`}>
			<div
				css={css`
                padding:1em;
                text-align:center;
                font-size:2em;
                font-weight: 600;
                `}
			>
				{pageTitle}
			</div>
			{isCompleteRegistrationPage && (
				<div
					css={css`
                    text-align:center;
                    font-size:1em;
                `}
				>
					{resetPasswordFormState.translate(
						"Enter a password to complete your registration.",
					)}
				</div>
			)}
			{isChangePasswordPage && (
				<div
					css={css`
					text-align:center;
					font-size:1em;
				`}
				>
					{resetPasswordFormState.translate("Enter your new password.")}
				</div>
			)}
			<div>
				<form
					key={`${resetPasswordFormState.get().emailValue}|${resetPasswordFormState.get().temporaryPasswordValue}`}
					noValidate
					autoComplete="off"
					onSubmit={(event) => {
						event.preventDefault();
						event.stopPropagation();
						void completeResetForm.handleSubmit();
					}}
				>
					{resetPasswordFormState.get().sendResetPasswordEmailErrorMessage && (
						<div
							css={css`
							padding:0 .5em;
							[data-slot="alert-description"] {
								white-space: pre;
							}
							`}
						>
							<Alert variant="destructive">
								<AlertCircle />
								<AlertDescription>
									{resetPasswordFormState.get()
										.sendResetPasswordEmailErrorMessage ?? ""}
								</AlertDescription>
							</Alert>
						</div>
					)}
					{resetPasswordFormState.get().isSendResetPasswordEmailSuccessful && (
						<Alert>
							<CheckCircle2 />
							<AlertDescription>
								{resetPasswordFormState.translate("Login successful")}
							</AlertDescription>
						</Alert>
					)}

					{!resetPasswordFormState.get().temporaryPasswordValue && (
						<div>
							<Alert>
								<Info />
								<AlertDescription>
									{resetPasswordFormState.translate(
										"Enter your email address and click the 'Reset Password' button to receive an email with an active link to reset your password.",
									)}
								</AlertDescription>
							</Alert>
						</div>
					)}

					<div
						css={css`display:flex;justify-content:center;align-items:center;margin:16px 0 ${fieldSpacing} 0`}
					>
						<div css={css`flex:1;`}>
							<completeResetForm.Field
								name="email"
								validators={{
									onSubmit: ({ value }) => {
										if (disableEmail) {
											return undefined;
										}
										const email = value.trim();
										if (email.length === 0) {
											return resetPasswordFormState.translate(
												"E-mail is required",
											);
										}
										if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
											return resetPasswordFormState.translate(
												"Enter a valid email address",
											);
										}
										return undefined;
									},
								}}
							>
								{(field) => {
									const showError =
										field.state.meta.isTouched ||
										completeResetForm.state.submissionAttempts > 0;
									const error = getResetFieldError(field.state.meta.errors);
									return (
										<Field>
											<FieldLabel
												className="mb-1 inline-block"
												htmlFor="txtEmail"
											>
												{resetPasswordFormState.translate("E-mail")}
												{!disableEmail && (
													<>
														{" "}
														<span className="text-destructive">*</span>
													</>
												)}
											</FieldLabel>
											{showError && error.length > 0 && (
												<FieldError>{error}</FieldError>
											)}
											<Input
												css={css`flex:1;`}
												id="txtEmail"
												placeholder={resetPasswordFormState.translate("E-mail")}
												type="text"
												autoComplete="username"
												required={!disableEmail}
												disabled={disableEmail}
												value={field.state.value}
												maxLength={255}
												onChange={(e) => {
													field.handleChange(e.currentTarget.value);
												}}
												onBlur={field.handleBlur}
												aria-invalid={showError && error.length > 0}
											/>
										</Field>
									);
								}}
							</completeResetForm.Field>
						</div>
					</div>

					{hasTemporaryPassword && (
						<div
							css={css`display:flex;justify-content:center;align-items:center;margin-bottom:${fieldSpacing};`}
						>
							<div css={css`position:relative;flex:1;`}>
								<completeResetForm.Field
									name="newPassword"
									validators={{
										onSubmit: ({ value }) =>
											hasTemporaryPassword && value.trim().length === 0
												? resetPasswordFormState.translate(
														"New Password is required",
													)
												: undefined,
									}}
								>
									{(field) => {
										const showError =
											field.state.meta.isTouched ||
											completeResetForm.state.submissionAttempts > 0;
										const error = getResetFieldError(field.state.meta.errors);
										return (
											<Field>
												<FieldLabel
													className="mb-1 inline-block"
													htmlFor="txtNewPassword"
												>
													{resetPasswordFormState.translate("New Password")}{" "}
													<span className="text-destructive">*</span>
												</FieldLabel>
												{showError && error.length > 0 && (
													<FieldError>{error}</FieldError>
												)}
												<Input
													css={css`flex:1;`}
													id="txtNewPassword"
													placeholder={resetPasswordFormState.translate(
														"New Password",
													)}
													type={showPassword ? "text" : "password"}
													autoComplete="new-password"
													required
													disabled={isFormDisabled}
													value={field.state.value}
													maxLength={20}
													className="pr-10"
													onChange={(e) => {
														field.handleChange(e.currentTarget.value);
													}}
													onBlur={field.handleBlur}
													aria-invalid={showError && error.length > 0}
												/>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													aria-label="toggle password visibility"
													className="absolute right-0 top-[22px] h-8 w-8"
													onClick={() => setShowPassword((prev) => !prev)}
													onMouseDown={(e) => e.preventDefault()}
													disabled={isFormDisabled}
												>
													{showPassword ? <Eye /> : <EyeOff />}
												</Button>
											</Field>
										);
									}}
								</completeResetForm.Field>
							</div>
						</div>
					)}

					<div
						css={css`
                        display:flex;
                        margin-bottom:${fieldSpacing};
                        align-items:center;
                        justify-content:center;
                        margin:1em 0 0 0;`}
					>
						<Button
							type="submit"
							variant="outline"
							disabled={isFormDisabled || completeResetForm.state.isSubmitting}
						>
							{resetPasswordFormState.translate("Submit")}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};
